import * as React from 'react'

import { cn } from '@/lib/utils'

type FieldProps = React.ComponentProps<'div'> & {
  orientation?: 'vertical' | 'horizontal' | 'responsive'
  'data-invalid'?: boolean | string
}

function Field({ className, orientation = 'vertical', ...props }: FieldProps) {
  return (
    <div
      data-slot='field'
      data-orientation={orientation}
      className={cn(
        'flex gap-2',
        orientation === 'vertical' && 'flex-col',
        orientation === 'horizontal' && 'flex-row items-center',
        orientation === 'responsive' && 'flex-col md:flex-row md:items-center',
        className
      )}
      {...props}
    />
  )
}

function FieldLabel({ className, ...props }: React.ComponentProps<'label'>) {
  return (
    <label
      data-slot='field-label'
      className={cn(
        'text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
        className
      )}
      {...props}
    />
  )
}

function FieldLegend({ className, ...props }: React.ComponentProps<'legend'>) {
  return (
    <legend
      data-slot='field-legend'
      className={cn('text-sm leading-none font-medium', className)}
      {...props}
    />
  )
}

function FieldDescription({ className, ...props }: React.ComponentProps<'p'>) {
  return (
    <p
      data-slot='field-description'
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  )
}

function FieldError({ className, ...props }: React.ComponentProps<'p'>) {
  return (
    <p
      data-slot='field-error'
      role='alert'
      className={cn('text-destructive text-sm', className)}
      {...props}
    />
  )
}

function FieldSet({ className, ...props }: React.ComponentProps<'fieldset'>) {
  return (
    <fieldset
      data-slot='fieldset'
      className={cn('flex flex-col gap-3', className)}
      {...props}
    />
  )
}

function FieldSeparator({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='field-separator'
      className={cn('bg-border h-px w-full', className)}
      {...props}
    />
  )
}

function FieldGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='field-group'
      className={cn('flex flex-col gap-4', className)}
      {...props}
    />
  )
}

export {
  Field,
  FieldLabel,
  FieldLegend,
  FieldDescription,
  FieldError,
  FieldSet,
  FieldSeparator,
  FieldGroup
}
